import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Check, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { useBackgroundTasks } from "../../hooks/useBackgroundTasks";
import {
  EventRun,
  stopRequiresConfirmation,
} from "../../models/audit/eventRun";
import {
  eventRunWorkpaperPath,
  getEventRunParent,
} from "../../lib/eventRunParent";
import { useAuth } from "../../contexts/authContext";
import { useWorkStepsEnabled } from "../../hooks/useWorkStepsEnabled";
import type { SessionKind } from "../../hooks/audit/useControlSessionEnvelope";
import { cn } from "../../lib/utils";
import { useHookCacheContextIfAvailable } from "../../lib/hookCache";
import { useOptroImportIfAvailable } from "../../contexts/optroImportContext";
import { useWorkStepOptroImportIfAvailable } from "../../contexts/workStepOptroImportContext";
import {
  STOP_RESPONSE_UNCONFIRMED,
  STOP_RESPONSE_UNPARSED,
  useStopEventRun,
} from "../../hooks/useStopEventRun";
import { StopEventRunButton } from "../audit/StopEventRunButton";
import { StopEventRunConfirmDialog } from "../audit/StopEventRunConfirmDialog";
import { ImportTasksSection } from "./ImportTasksSection";

// How long a manual refresh spins the icon at minimum, so the click visibly
// registers even when the reload finishes almost instantly.
const MIN_REFRESH_SPIN_MS = 1000;
// How long the check shows after a refresh before the icon returns.
const REFRESH_DONE_MS = 1000;

const ENTITY_LABELS: Record<SessionKind, string> = {
  control: "Control",
  work_step: "Work Step",
};

// A run is eligible for the stop control while its top-level state is still
// in flight. Unlike EventRunsPopover, this surface renders outside
// SessionRunsProvider — no per-sample sessionRuns are available here, so
// eligibility is state-based only.
const isActiveEventRunState = (state: EventRun["state"]): boolean =>
  state === "pending" || state === "processing";

const getStateColor = (state: EventRun["state"]): string => {
  switch (state) {
    case "completed":
      return "bg-green-100 text-green-800 border-green-300";
    case "failed":
      return "bg-red-100 text-red-800 border-red-300";
    case "cancelled":
      return "bg-gray-100 text-gray-800 border-gray-300";
    case "processing":
      return "bg-yellow-100 text-yellow-800 border-yellow-300";
    case "pending":
      return "bg-blue-100 text-blue-800 border-blue-300";
    default:
      return "bg-gray-100 text-gray-800 border-gray-300";
  }
};

const getStateLabel = (state: EventRun["state"]): string => {
  switch (state) {
    case "completed":
      return "Completed";
    case "failed":
      return "Failed";
    case "cancelled":
      return "Cancelled";
    case "processing":
      return "Processing";
    case "pending":
      return "Pending";
    case "unknown":
      return "Unknown";
    default:
      return state;
  }
};

interface BackgroundTasksPopoverProps {
  isCollapsed?: boolean;
  // Reports open/close so the sidebar container can stay expanded while the
  // popover is open. The popover content is portaled outside the sidebar's
  // hover-bearing element, so the sidebar can't infer this from mouse events
  // alone.
  onOpenChange?: (isOpen: boolean) => void;
}

export const BackgroundTasksPopover = ({
  isCollapsed = false,
  onOpenChange,
}: BackgroundTasksPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  // Ref'd so the unmount cleanup below doesn't re-run when the caller passes
  // a fresh callback, and so it sees the latest open state.
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  const isOpenRef = useRef(false);
  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      isOpenRef.current = open;
      onOpenChange?.(open);
    },
    [onOpenChange],
  );
  // Radix fires no onOpenChange when the open popover simply stops rendering
  // (unmount) — synthesize the close so the sidebar container's
  // `isBackgroundTasksOpen` doesn't wedge the sidebar expanded.
  useEffect(
    () => () => {
      if (isOpenRef.current) onOpenChangeRef.current?.(false);
    },
    [],
  );
  const navigate = useNavigate();
  const { user } = useAuth();
  // Work step routes redirect to the controls list while the flag is off, so
  // a work step run's card stays informational rather than linking there.
  const workStepsEnabled = useWorkStepsEnabled();
  const { eventRuns, isLoading, isLoadingMore, hasMore, refetch, loadMore } =
    useBackgroundTasks({
      userId: user?.id,
      limit: 30,
      refreshInterval: isOpen ? 10000 : 60000, // Refresh every 5 seconds when open, 30 seconds when closed
      enabled: true, // Always fetch to keep tasks updated
    });
  const { stopEventRun, isStopping } = useStopEventRun();
  // The event run awaiting workpaper-scope confirmation, if any.
  const [confirmDialogEventRun, setConfirmDialogEventRun] =
    useState<EventRun | null>(null);
  // Scopes `loading` to the buttons whose stops are in flight — a Set, so a
  // second concurrent stop doesn't clobber the first card's spinner.
  const [stoppingEventRunIds, setStoppingEventRunIds] = useState<Set<string>>(
    new Set(),
  );

  const observer = useRef<IntersectionObserver | null>(null);
  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading || isLoadingMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, isLoadingMore, hasMore, loadMore],
  );

  // Null when no import tracker is mounted above the sidebar.
  const controlImports = useOptroImportIfAvailable();
  const workStepImports = useWorkStepOptroImportIfAvailable();

  // A manual refresh spins the icon, then shows a check before returning to
  // the static icon. The lists update in place, so this is the only sign the
  // click registered: the spin lasts at least MIN_REFRESH_SPIN_MS even when
  // the reload is faster than that.
  const [refreshState, setRefreshState] = useState<
    "idle" | "refreshing" | "done"
  >("idle");
  const refreshDoneTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (refreshDoneTimer.current) clearTimeout(refreshDoneTimer.current);
    },
    [],
  );

  // Reloads everything the popover lists: task runs and imports.
  const handleRefresh = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (refreshState !== "idle") return;
    setRefreshState("refreshing");
    controlImports?.refreshImports();
    workStepImports?.refreshImports();
    await Promise.all([
      refetch(),
      new Promise((resolve) => setTimeout(resolve, MIN_REFRESH_SPIN_MS)),
    ]);
    setRefreshState("done");
    refreshDoneTimer.current = setTimeout(
      () => setRefreshState("idle"),
      REFRESH_DONE_MS,
    );
  };

  // Nullable on purpose: the sidebar renders on every route but only the
  // audit workpaper route carries a HookCacheProvider. Null means no cached
  // workpaper data exists to go stale, so the invalidation below no-ops.
  const hookCache = useHookCacheContextIfAvailable();

  // Refreshes the workpaper-view caches for the stopped run's procedure
  // session, under whichever parent (control or work step) it belongs to.
  // This surface renders outside `SessionRunsProvider`, so
  // `useSessionRunsTransitions` never observes the stop from here; and for
  // an already-settled run (the zombie-recovery case) the status poller sees
  // no not-ready→ready flip either — this is the grid's only refresh path
  // for a sidebar-initiated stop. Session-scope keys only: this surface has
  // no per-row scope to narrow the invalidation with.
  const invalidateStoppedRunCaches = useCallback(
    (eventRun: EventRun) => {
      const parent = getEventRunParent(eventRun.runMetadata);
      if (!hookCache || !parent) return;
      void hookCache.invalidate([
        "procedure-session",
        parent.procedureSessionId,
        "table-shell",
      ]);
      void hookCache.invalidate([
        "procedure-session",
        parent.procedureSessionId,
        "attribute-test-result",
      ]);
      void hookCache.invalidate(["workpaper-data", parent.procedureSessionId]);
      // A prefix: also covers the work step envelope's
      // `["control-session", id, "work-step-envelope"]` key.
      void hookCache.invalidate(["control-session", parent.parentSessionId]);
    },
    [hookCache],
  );

  // Calls the stop endpoint and applies the shared success UX: toast, skip
  // warning, then refetch so the card flips to Cancelled — plus the cache
  // invalidation above so an open workpaper grid catches up. No highlight
  // seeding here — the grid isn't mounted on this surface.
  const performStop = useCallback(
    async (eventRun: EventRun) => {
      setStoppingEventRunIds((prev) => new Set(prev).add(eventRun.id));
      const response = await stopEventRun(eventRun.id);
      setStoppingEventRunIds((prev) => {
        const next = new Set(prev);
        next.delete(eventRun.id);
        return next;
      });
      setConfirmDialogEventRun(null);

      if (!response) {
        // Hard failure (hook already toasted it). The backend's stop is
        // several separate commits, so a throw partway can leave the run
        // fenced to cancelled with the card still showing Running — refetch
        // and invalidate so the UI reflects whatever actually landed.
        await refetch();
        invalidateStoppedRunCaches(eventRun);
        return;
      }

      if (
        response === STOP_RESPONSE_UNPARSED ||
        response === STOP_RESPONSE_UNCONFIRMED
      ) {
        // Stop landed but the response was unusable, or the transport failed
        // with the outcome unknown (hook already toasted either way) — still
        // refetch and invalidate so a landed stop flips the card and grid.
        await refetch();
        invalidateStoppedRunCaches(eventRun);
        return;
      }

      if (response.alreadyFinished) {
        toast.info("Run had already finished — swept any stragglers");
      } else {
        toast.success("Run stopped");
      }
      if (response.skipped.length > 0) {
        toast.warning(
          `${response.skipped.length} cell(s) kept — edited or locked by a user`,
        );
      }

      await refetch();
      invalidateStoppedRunCaches(eventRun);
    },
    [stopEventRun, refetch, invalidateStoppedRunCaches],
  );

  const handleStopClick = (eventRun: EventRun) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (stopRequiresConfirmation(eventRun.runMetadata)) {
      setConfirmDialogEventRun(eventRun);
      return;
    }
    void performStop(eventRun);
  };

  const getActiveTasks = () => {
    return eventRuns.filter(
      (run) => run.state === "processing" || run.state === "pending",
    );
  };

  const activeTasks = getActiveTasks();
  const hasRunningImports = [controlImports, workStepImports].some((tracker) =>
    tracker?.jobs.some((tracked) => !tracked.job.isDone),
  );
  const hasActiveTasks = activeTasks.length > 0 || hasRunningImports;
  // The Imports section renders whenever either tracker holds an import.
  const hasImports = [controlImports, workStepImports].some(
    (tracker) => tracker !== null && tracker.jobs.length > 0,
  );

  return (
    <>
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              "w-full p-3 relative font-normal",
              // Collapsed: centre the icon and drop the gap, so this row lines
              // up with the SidebarLink rows above it. The label collapses to
              // `w-0` rather than unmounting, and a gap still reserves space
              // beside a zero-width element.
              isCollapsed ? "justify-center gap-0" : "justify-start gap-2",
            )}
          >
            <Activity className="h-5 w-5 shrink-0" />
            <span
              className={cn(
                "whitespace-nowrap overflow-hidden transition-all duration-300",
                isCollapsed ? "w-0 opacity-0" : "w-auto opacity-100",
              )}
            >
              Background Tasks
            </span>
            {hasActiveTasks && (
              <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-yellow-500 animate-pulse pointer-events-none" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[450px] max-h-[600px] overflow-y-auto"
          align="start"
          side="right"
          sideOffset={20}
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-2 border-b">
              <h4 className="font-semibold text-base text-gray-900">
                Background Tasks
              </h4>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "h-7 w-7",
                  refreshState === "done" &&
                    "bg-emerald-50 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-600 disabled:opacity-100",
                )}
                onClick={handleRefresh}
                disabled={refreshState !== "idle"}
                aria-label={
                  refreshState === "done" ? "Tasks refreshed" : "Refresh tasks"
                }
              >
                {refreshState === "done" ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <RefreshCw
                    className={cn(
                      "h-4 w-4",
                      refreshState === "refreshing" && "animate-spin",
                    )}
                  />
                )}
              </Button>
            </div>

            <ImportTasksSection onOpenImport={() => handleOpenChange(false)} />

            {isLoading && eventRuns.length === 0 ? (
              <div className="text-sm text-gray-500 py-4 text-center">
                Loading tasks...
              </div>
            ) : eventRuns.length === 0 ? (
              !hasImports && (
                <div className="text-sm text-gray-500 py-4 text-center">
                  No background tasks found
                </div>
              )
            ) : (
              <div className="space-y-2">
                {eventRuns.map((eventRun, index) => {
                  const parent = getEventRunParent(eventRun.runMetadata);
                  const isClickable =
                    parent !== null &&
                    (parent.kind !== "work_step" || workStepsEnabled);

                  const handleClick = () => {
                    if (!parent) return;
                    navigate(eventRunWorkpaperPath(parent, eventRun.id));
                    handleOpenChange(false);
                  };

                  const displayName =
                    eventRun.runMetadata?.displayName ||
                    `Session: ${eventRun.sessionId.slice(0, 8)}...`;

                  const isLastElement = index === eventRuns.length - 1;

                  return (
                    <div
                      key={eventRun.id}
                      data-testid="background-task-card"
                      ref={isLastElement ? lastElementRef : null}
                      onClick={isClickable ? handleClick : undefined}
                      className={`border p-4 space-y-3 transition-all hover:shadow-md hover:border-gray-300 bg-white ${
                        isClickable ? "cursor-pointer" : ""
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <h5 className="text-sm font-semibold text-gray-900 flex-1 min-w-0 break-words">
                          {displayName}
                        </h5>
                        <Badge
                          variant="outline"
                          className={`text-xs font-medium shrink-0 ${getStateColor(eventRun.state)}`}
                        >
                          {getStateLabel(eventRun.state)}
                        </Badge>
                        {isActiveEventRunState(eventRun.state) && (
                          <StopEventRunButton
                            onClick={handleStopClick(eventRun)}
                            isStopping={stoppingEventRunIds.has(eventRun.id)}
                          />
                        )}
                      </div>

                      <div className="space-y-1 pt-2 border-t border-gray-100">
                        {parent?.entityName && (
                          <div className="flex items-start text-xs text-gray-600 gap-2">
                            <span className="font-medium shrink-0 w-20">
                              {ENTITY_LABELS[parent.kind]}:
                            </span>
                            <span
                              data-testid={
                                parent.kind === "work_step"
                                  ? "background-task-work-step-value"
                                  : "background-task-control-value"
                              }
                              className="flex-1 min-w-0 line-clamp-2 break-words leading-relaxed"
                            >
                              {parent.entityName}
                            </span>
                          </div>
                        )}
                        {parent?.parentSessionName && (
                          <div className="flex items-center text-xs text-gray-600 gap-2">
                            <span className="font-medium shrink-0 w-20">
                              Test Session:
                            </span>
                            <span
                              data-testid="background-task-test-session-value"
                              className="flex-1 min-w-0"
                            >
                              {parent.parentSessionName}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center text-xs text-gray-600 gap-2">
                          <span className="font-medium shrink-0 w-20">
                            Started at:
                          </span>
                          <span
                            data-testid="background-task-started-at-value"
                            className="flex-1 min-w-0"
                          >
                            {new Date(eventRun.createdAt).toLocaleString()}
                          </span>
                        </div>

                        {eventRun.state === "completed" &&
                          eventRun.updatedAt &&
                          eventRun.createdAt !== eventRun.updatedAt && (
                            <div className="flex items-center text-xs text-gray-600 gap-2">
                              <span className="font-medium shrink-0 w-20">
                                Completed at:
                              </span>
                              <span
                                data-testid="background-task-completed-at-value"
                                className="flex-1 min-w-0"
                              >
                                {new Date(eventRun.updatedAt).toLocaleString()}
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                  );
                })}
                {isLoadingMore && (
                  <div className="text-sm text-gray-500 py-4 text-center">
                    Loading more tasks...
                  </div>
                )}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
      {confirmDialogEventRun && (
        <StopEventRunConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setConfirmDialogEventRun(null);
          }}
          isStopping={isStopping}
          onConfirm={() => void performStop(confirmDialogEventRun)}
        />
      )}
    </>
  );
};
